<template>
    <div
        class="pds-stack justify-center align-center mt-40 mb-20 pagination"
        :class="props.class"
    >
        <button
            v-if="total >= 5"
            type="button"
            class="pagination__btn mr-4"
            :disabled="prevDisabled"
            @click="goToFirst"
        >
            <IcnPdsFirstpage size="20" />
        </button>
        <button
            type="button"
            class="pagination__btn mr-4"
            :disabled="prevDisabled"
            @click="goToPrev"
        >
            <IcnPdsArrowLeft size="20" />
        </button>
        <button
            v-for="(page, idx) in pageList"
            :key="`${page}-${idx}`"
            type="button"
            class="pagination__btn pds-typo-body-md-regular"
            :class="{active: currentPage === page}"
            :disabled="typeof page === 'string'"
            @click="typeof page === 'number' && goToPage(page)"
        >
            <span>{{ page }}</span>
        </button>
        <button
            type="button"
            class="pagination__btn ml-4"
            :disabled="nextDisabled"
            @click="goToNext"
        >
            <IcnPdsArrowRight size="20" />
        </button>
        <button
            v-if="total >= 5"
            type="button"
            class="pagination__btn ml-4"
            :disabled="nextDisabled"
            @click="goToLast"
        >
            <IcnPdsLastpage size="20" />
        </button>
    </div>
</template>

<script setup lang="ts">
import {toRef} from 'vue';

import {
    IcnPdsArrowLeft,
    IcnPdsArrowRight,
    IcnPdsFirstpage,
    IcnPdsLastpage,
} from '@/libs/pds/icons';

import {usePagination} from './usePagination';

type Props = {
    total: number;
    currentPage: number;
    class?: string;
};

const props = withDefaults(defineProps<Props>(), {
    total: 1,
    currentPage: 1,
});

const emit = defineEmits<{
    (e: 'change', page: number): void;
}>();

const {pageList, prevDisabled, nextDisabled} = usePagination({
    total: toRef(props, 'total'),
    currentPage: toRef(props, 'currentPage'),
});

function goToFirst() {
    if (props.currentPage !== 1) {
        emit('change', 1);
    }
}

function goToPrev() {
    if (props.currentPage > 1) {
        emit('change', props.currentPage - 1);
    }
}

function goToNext() {
    if (props.currentPage < props.total) {
        emit('change', props.currentPage + 1);
    }
}

function goToLast() {
    if (props.currentPage !== props.total) {
        emit('change', props.total);
    }
}

function goToPage(page: number) {
    emit('change', page);
}
</script>

<style lang="scss" scoped>
@use '@pds/style/variables/variables' as pds;

.pagination {
    width: 100%;
}

.pagination__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    color: pds.$pds-color-alias-foreground-primary;
    background-color: pds.$pds-color-alias-background-primary;
    border: none;
    cursor: pointer;

    &:hover:not(:disabled) {
        background-color: pds.$pds-color-alias-button-outlined-secondary-bgHover;
        border-radius: 999px;
    }

    &:disabled {
        pointer-events: none;
        cursor: not-allowed;
        opacity: 0.3;
    }

    &.active {
        background-color: pds.$pds-color-alias-button-primary-default;
        border-radius: 999px;
        color: pds.$pds-color-alias-foreground-inverse-primary;
        pointer-events: none;
    }
}
</style>
